import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";
import { SelectTrigger } from "@/components/ui/select";
import { cn } from "@/lib/utils";

interface FilterChipTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  /** Whether the underlying filter has a non-default value. Drives the active styling. */
  active?: boolean;
}

/**
 * Pill-shaped Select trigger used by the Objectives filter bar.
 *
 * Wraps the existing shadcn `SelectTrigger` so all keyboard, focus, and ARIA
 * behaviour stays identical to the rest of the app — only the visual shell
 * changes. `active` toggles a navy fill when the user has selected a non-default value.
 */
export const FilterChipTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  FilterChipTriggerProps
>(({ className, children, active, ...props }, ref) => (
  <SelectTrigger
    ref={ref}
    className={cn(
      "h-9 w-auto gap-1.5 rounded-full border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--foreground))] hover:border-[hsl(var(--ring))]",
      // shadcn SelectTrigger renders a built-in ChevronDown via SelectPrimitive.Icon.
      // Hide it so we can render a smaller chip-style caret.
      "[&>svg:last-child]:hidden",
      active &&
        "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] font-semibold text-[hsl(var(--primary))]",
      className,
    )}
    {...props}
  >
    {children}
    <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
  </SelectTrigger>
));
FilterChipTrigger.displayName = "FilterChipTrigger";
